// Formatting rules for the Smart Matching Resume Specification v1.1.
// These helpers turn the structured data collected in the wizard into the exact
// strings the spec dictates. They are pure (no DOM) so both the server PDF
// template and the live HTML preview share one source of truth, and so the rules
// can be unit-tested on their own.

// §9 — common academic degree abbreviations. Exact letter order must be kept,
// even inside a Hebrew (RTL) document.
export const DEGREE_ABBREVIATIONS = {
  'תואר ראשון במשפטים': 'LL.B.',
  'תואר שני במשפטים': 'LL.M.',
  'תואר ראשון בכלכלה': 'B.A.',
  'תואר ראשון במדעי החברה': 'B.A.',
  'תואר שני במנהל עסקים': 'M.B.A.',
  'תואר ראשון במדעים': 'B.Sc.',
  'תואר שני במדעים': 'M.Sc.',
  'תואר ראשון בהנדסה': 'B.Sc.',
  'תואר שני בהנדסה': 'M.Sc.',
  'תואר ברפואה': 'M.D.'
};

const clean = (value) => String(value ?? '').trim();

// §21 — the average is shown ONLY when it is strictly greater than 90.
export const shouldShowAverage = (average) => {
  const numeric = Number(average);
  return Number.isFinite(numeric) && numeric > 90;
};

export const suggestDegreeAbbreviation = (degreeName) => DEGREE_ABBREVIATIONS[clean(degreeName)] || '';

// Ensure a full sentence ends with a single period (§8, §11, §12, §18).
export const ensureSentencePeriod = (text) => {
  const value = clean(text);
  if (!value) return '';
  return /[.!?…]$/.test(value) ? value : `${value}.`;
};

// §9 — a single education line, gold-rail layout. The institution LEADS the line
// (rendered bold by both the PDF template and the preview), the degree spelled
// out with its academic abbreviation in parentheses follows after " | ", then
// optional specialization / average(>90) / honors. Returned split so the
// renderer can bold the institution. No trailing period (info line). Year ranges
// use an en dash (–).
export const formatEducationLine = (entry = {}) => {
  const startYear = clean(entry.startYear);
  const endYear = clean(entry.endYear);
  const years = [startYear, endYear].filter(Boolean).join('–');

  const degreeName = clean(entry.degreeName);
  const abbreviation = clean(entry.degreeAbbreviation) || suggestDegreeAbbreviation(degreeName);
  const institution = clean(entry.institution);
  const specialization = clean(entry.specialization);
  const honors = clean(entry.honors);

  let details = degreeName;
  if (abbreviation) details += ` (${abbreviation})`;
  if (specialization) details += ` עם ${specialization}`;
  if (shouldShowAverage(entry.average)) details += `, ממוצע ${Number(entry.average)}`;
  if (honors) details += `, ${honors}`;

  return { years, institution, details: details.trim() };
};

// §10 — experience period. Month/year, no leading zeros, en dash range, no spaces
// around it. An ongoing role ends with "היום".
export const formatExperiencePeriod = (entry = {}) => {
  const start = [entry.startMonth, entry.startYear].map(clean).filter(Boolean).join('/');
  const end = entry.current
    ? 'היום'
    : [entry.endMonth, entry.endYear].map(clean).filter(Boolean).join('/');
  return [start, end].filter(Boolean).join('–');
};

// §10 — "company | role" (company is emphasised at the rendering layer).
export const formatExperienceHeading = (entry = {}) => {
  const company = clean(entry.company);
  const role = clean(entry.role);
  return [company, role].filter(Boolean).join(' | ');
};

// §13 — languages on one line: "עברית — שפת אם · אנגלית — רמה גבוהה" (em dash
// between language and level, spaced middot between items, no trailing period).
export const formatLanguages = (languages = []) => (
  languages
    .map((item) => [clean(item.language), clean(item.level)].filter(Boolean).join(' — '))
    .filter(Boolean)
    .join(' · ')
);

// §15 — computer skills / software on one line separated by " · ".
export const formatComputerSkills = (skills = []) => (
  skills.map(clean).filter(Boolean).join(' · ')
);

// §14 — military service line: "years \t unit | role" (en dash year range).
export const formatMilitaryLine = (entry = {}) => {
  const years = [clean(entry.startYear), clean(entry.endYear)].filter(Boolean).join('–');
  const detail = [clean(entry.unit), clean(entry.role)].filter(Boolean).join(' | ');
  return { years, detail };
};

// §16 — course / certification line: "year \t name".
export const formatCourseLine = (entry = {}) => ({
  year: clean(entry.year),
  name: clean(entry.name)
});

// Contact line under the name: "phone · email · linkedin", omitting blanks (§5).
export const formatContact = (identity = {}) => (
  [clean(identity.phone), clean(identity.email), clean(identity.linkedin)]
    .filter(Boolean)
    .join(' · ')
);

// Parse a 4-digit year into a number for sorting; blanks sort last.
const yearValue = (value) => {
  const numeric = parseInt(clean(value), 10);
  return Number.isFinite(numeric) ? numeric : -Infinity;
};

// Sort entries most-recent-first by the given year accessor, regardless of the
// order the user typed them in. Stable: entries with equal (or missing) years
// keep their relative order.
const sortByYearDesc = (entries, getYear) => (
  entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => (getYear(b.entry) - getYear(a.entry)) || (a.index - b.index))
    .map((item) => item.entry)
);

const educationHasContent = (entry) => Boolean(clean(entry.degreeName) || clean(entry.institution));
const experienceHasContent = (entry) => Boolean(clean(entry.company) || clean(entry.role) || (entry.bullets || []).some(clean));
const skillHasContent = (entry) => Boolean(clean(entry.description));
const languageHasContent = (entry) => Boolean(clean(entry.language));
const militaryHasContent = (entry) => Boolean(clean(entry.unit) || clean(entry.role));
const courseHasContent = (entry) => Boolean(clean(entry.name));

// Normalise the raw wizard state into only the categories/entries that actually
// carry content, in the spec's display order (§6). Bullets/sentences get their
// trailing period here so the PDF template and preview stay dumb.
export const buildResumeModel = (data = {}) => {
  const education = sortByYearDesc(
    (data.education || []).filter(educationHasContent),
    (entry) => Math.max(yearValue(entry.endYear), yearValue(entry.startYear))
  ).map((entry) => ({
    ...formatEducationLine(entry)
  }));

  const experience = sortByYearDesc(
    (data.experience || []).filter(experienceHasContent),
    // Ongoing roles rank first; otherwise most recent end (or start) year wins.
    (entry) => (entry.current ? Infinity : Math.max(yearValue(entry.endYear), yearValue(entry.startYear)))
  ).map((entry) => ({
    period: formatExperiencePeriod(entry),
    company: clean(entry.company),
    role: clean(entry.role),
    heading: formatExperienceHeading(entry),
    // Experience bullets are short fragments, shown without a trailing period
    // (matches the reference resume format).
    bullets: (entry.bullets || []).map(clean).filter(Boolean)
  }));

  const relevantSkills = (data.relevantSkills || []).filter(skillHasContent).map((entry) => ({
    title: clean(entry.title),
    description: ensureSentencePeriod(entry.description)
  }));

  const languages = formatLanguages((data.languages || []).filter(languageHasContent));
  const computerSkills = formatComputerSkills(data.computerSkills || []);

  const militaryService = (data.militaryService || []).filter(militaryHasContent).map(formatMilitaryLine);
  const courses = sortByYearDesc(
    (data.courses || []).filter(courseHasContent),
    (entry) => yearValue(entry.year)
  ).map(formatCourseLine);

  return {
    summary: ensureSentencePeriod(data.summary),
    education,
    experience,
    relevantSkills,
    languages,
    militaryService,
    computerSkills,
    courses
  };
};

// True when there is at least one piece of resume content worth generating.
export const resumeModelHasContent = (model) => Boolean(
  model.summary ||
  model.education.length ||
  model.experience.length ||
  model.relevantSkills.length ||
  model.languages ||
  model.militaryService.length ||
  model.computerSkills ||
  model.courses.length
);

// Spec §7 category titles, in display order.
export const CATEGORY_TITLES = {
  summary: 'תמצית',
  education: 'השכלה',
  experience: 'ניסיון תעסוקתי',
  relevantSkills: 'כישורים',
  languages: 'שפות',
  militaryService: 'שירות צבאי',
  computerSkills: 'תוכנות',
  courses: 'קורסים והסמכות'
};
